import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import Colors from 'colors.json'

import Input from 'components/Input'
import Button from 'components/Button'
import Dropdown from 'components/Dropdown'
import { Form, Formik } from 'formik'
import DatetimePickerInput from 'components/DatetimePickerInput'

import { MemberProfileUpdateSchemas } from 'schemas/profileUpdateDetailsSchemas'
import { useProfileUpdate } from './useUpdateDetails'
import { MEMBER_GENDER } from 'constants/filters'
import MerchantLookup from './HomeStoreSearch'
import { getMemberDetail } from 'api/members'
import useMemberStore from 'store/member'

import { isQSRHClient } from 'setups/controller'
import { projectClient } from 'constants/project'

const Index: React.FC<{ onCloseModal: () => void }> = ({
  onCloseModal,
}): React.ReactElement => {
  // STATES
  const { traderId } = useParams()
  const [enrollmentTraderName, setEnrollmentTraderName] = useState('')
  const member = useMemberStore((state) => state.member)
  const memberRankings = useMemberStore((state) => state.memberRankings)
  useEffect(() => {
    const fetchMemberDetails = async () => {
      if (!traderId) {
        console.warn('traderId is undefined')
        return
      }
      try {
        const response = await getMemberDetail(traderId)
        if (response.outcomeCode == 0 && response.enrollmentTraderName) {
          setEnrollmentTraderName(response.enrollmentTraderName)
        } else {
          setEnrollmentTraderName('')
        }
      } catch (error) {
        console.error('Error fetching member details:', error)
      }
    }

    fetchMemberDetails()
  }, [traderId])

  const {
    isLoading,
    initialValues,
    handleSubmitProfileUpdate,
    handleClearProfileUpdate,
  } = useProfileUpdate(Number(traderId), onCloseModal)

  if (isLoading) return <></>

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={MemberProfileUpdateSchemas}
      onSubmit={handleSubmitProfileUpdate}
    >
      {({ values, isSubmitting, setFieldValue, errors, touched }) => {
        console.log('Form Values:', values)
        console.log('Validation Errors:', errors)
        const d = new Date()
        const formatted =
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ` +
          `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`

        return (
          <Form>
            <div className="mb-4">
              <div className="flex gap-4 items-center">
                <Input
                  name="firstName"
                  label="First Name"
                  labelClassName="text-gray-500 w-full"
                />
                <Input
                  name="lastName"
                  label="Last Name"
                  labelClassName="text-gray-500 w-full"
                />
                <Input
                  name="birthDate"
                  label="Birthday"
                  labelClassName="text-gray-500"
                  placeholder="YYYY-MM-DD"
                // disabled
                />
              </div>

              <div className="flex gap-4 items-start">
                <div className="flex flex-col">
                  <Dropdown
                    title="Gender"
                    defaultValue={
                      MEMBER_GENDER.filter(
                        (item) => String(item.value) === String(values.genderId)
                      )[0]
                    }
                    options={MEMBER_GENDER}
                    className="w-40"
                    borderColor={errors.genderId ? 'red' : '#C4C4C4'}
                    onSelect={(item) => {
                      setFieldValue('genderId', Number(item.value))
                    }}
                  />
                  {errors.genderId && (
                    <div className="text-red text-xs mt-1 tracking-wider pb-2">
                      {errors.genderId}
                    </div>
                  )}
                </div>
                <Input
                  name="email"
                  label="Email"
                  labelClassName="text-gray-500"
                // disabled
                />
                <Input
                  name="mobile"
                  label="Mobile"
                  labelClassName="text-gray-500"
                />
              </div>
              <DatetimePickerInput
                title={'Enrolment Date'}
                setFieldName="enrollmentDate"
                setFieldValue={setFieldValue}
                showTimeSelect={true}
                defaultDateValue={values.enrollmentDate || formatted}
              />
              <MerchantLookup
                isMerchantLookupToggle={true}
                title="Home Store"
                placeholder={enrollmentTraderName || 'Search by merchant name...'}
                setFieldValue={(field, value) => {
                  if (field === 'storeId') {
                    // Set traderId for backend submission
                    setFieldValue('enrollmentTraderId', value)
                  }
                  // Set deviceCode for backend
                  setFieldValue('deviceCode', value)
                }}
              />
              <Input
                name="address1"
                label="Address 1"
                labelClassName="text-gray-500"
              />
              <Input
                name="address2"
                label="Address 2"
                labelClassName="text-gray-500"
              />
              <Input
                name="address3"
                label="Address 3"
                labelClassName="text-gray-500"
              />
              <Input
                name="address4"
                label="Address 4"
                labelClassName="text-gray-500"
              />
              <Input
                name="suburb"
                label="Suburb"
                labelClassName="text-gray-500"
              />
              <div className="flex gap-4 items-center">
                <Input
                  name="state"
                  label="State"
                  labelClassName="text-gray-500"
                />
                <Input
                  name="city"
                  label="City"
                  labelClassName="text-gray-500"
                />
                <Input
                  name="postCode"
                  label="Postal Code"
                  labelClassName="text-gray-500"
                />
              </div>

              <div className="flex gap-4 items-center">
                <Input
                  name="country"
                  label="Country"
                  labelClassName="text-gray-500"
                  disabled
                />
              </div>

              <div className="flex gap-4 items-center">
                <Dropdown
                  title="Member Ranking"
                  defaultValue={{
                    label: member?.ranking || '',
                    value: String(member?.rankingId),
                  }
                  }
                  options={memberRankings.map((ranking) => ({
                    label: ranking.traderRanking,
                    value: String(ranking.traderRankingId),
                  }))}
                  className="w-40 mb-5"
                  borderColor={errors.traderRankingId ? 'red' : '#C4C4C4'}
                  onSelect={(item) => {
                    setFieldValue('traderRankingId', Number(item.value))
                  }}
                  overflowStyle
                />
                {errors.traderRankingId && (
                  <div className="text-red text-xs mt-1 tracking-wider pb-2">
                    {errors.traderRankingId}
                  </div>
                )}
                {isQSRHClient(projectClient) && (
                  <Input
                    name="maxLoyaltyBalance"
                    label="Max Loyalty Balance"
                    labelClassName="text-gray-500"
                    className="mb-0"
                  />
                )}
              </div>

              <div className="flex gap-4 items-center">
                <Input
                  type="input"
                  name="comments"
                  label="Comment"
                  containerClassName="w-full"
                />
              </div>

              <div className="flex items-center mb-6">
                <label htmlFor="channelEmail" className="text-gray-500 mr-2 font-light">
                  Channel Email
                </label>
                <input
                  type="checkbox"
                  id="channelEmail"
                  name="channelEmail"
                  className="mr-2"
                  checked={values.channelEmail === 1}
                  onChange={(e) => {
                    setFieldValue('channelEmail', e.target.checked ? 1 : 0)
                  }}
                />
                <label htmlFor="channelEmail" className="text-gray-500 mr-2 font-light">
                  Channel SMS
                </label>
                <input
                  type="checkbox"
                  id="channelSMS"
                  name="channelSMS"
                  className="mr-2"
                  checked={values.channelSMS === 1}
                  onChange={(e) => {
                    setFieldValue('channelSMS', e.target.checked ? 1 : 0)
                  }}
                />
              </div>
            </div>

            <footer className="flex justify-end pb-4 pl-4 pr-4">
              <Button
                type="submit"
                className="mr-2 rounded bg-primary"
                disabled={isSubmitting}
              >
                <span className="tracking-wider font-bold">Confirm</span>
              </Button>
              <Button
                className="bg-white border rounded border-primary"
                onClick={onCloseModal}
                style={{ color: Colors.primary }}
              >
                <span className="tracking-wider font-bold">Cancel</span>
              </Button>
            </footer>
          </Form>
        )
      }}
    </Formik>
  )
}

export default Index
